import { useState } from "react";
import { Icon } from "../../../components/ui/Icon";
import { useSearchParams } from "react-router-dom";
import { YoutubePlayer } from "../../../components/youtube/YoutubePlayer";
import { Modal } from "../../../components/ui/Modal";
import {
  formatDuration,
  formatRelative,
  playlistWatchUrl,
  useYoutubePlaylist,
  useYoutubePlaylists,
  videoWatchUrl,
  type YoutubePlaylistSummary,
} from "../../../lib/youtube";

/**
 * Library → Series. Read-only playlist grid; click a card to open the
 * detail drawer with the embedded series + items list. Admin
 * affordances live elsewhere (the dashboard YouTube → Series tab).
 */

const PAGE_SIZE = 18;

export default function LibrarySeries() {
  const [params, setParams] = useSearchParams();
  const offset = Math.max(0, Number(params.get("offset") ?? "0"));
  const [openId, setOpenId] = useState<string | null>(null);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const queryRes = useYoutubePlaylists({ limit: PAGE_SIZE, offset });
  const playlists = queryRes.data?.playlists ?? [];
  const total = queryRes.data?.total ?? 0;

  return (
    <>
      <p className="yt-admin-count yt-public-count">
        {queryRes.isLoading
          ? "Loading…"
          : total === 0
            ? "No series yet."
            : `${total.toLocaleString()} series`}
      </p>

      {queryRes.isLoading ? (
        <SkeletonGrid count={PAGE_SIZE} />
      ) : playlists.length === 0 ? null : (
        <ul className="yt-public-grid is-comfortable">
          {playlists.map((p) => (
            <SeriesCard
              key={p.id}
              playlist={p}
              onOpen={() => setOpenId(p.id)}
            />
          ))}
        </ul>
      )}
      {total > PAGE_SIZE && (
        <nav className="yt-pagination yt-public-pagination" aria-label="Pagination">
          <button
            type="button"
            className="btn-ghost"
            onClick={() =>
              setParam("offset", Math.max(0, offset - PAGE_SIZE) > 0 ? String(Math.max(0, offset - PAGE_SIZE)) : null)
            }
            disabled={offset <= 0}
          >
            <span>Newer</span>
          </button>
          <span className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)]">
            Page {Math.floor(offset / PAGE_SIZE) + 1} of{" "}
            {Math.max(1, Math.ceil(total / PAGE_SIZE))}
          </span>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => setParam("offset", String(offset + PAGE_SIZE))}
            disabled={offset + PAGE_SIZE >= total}
          >
            <span>Older</span>
          </button>
        </nav>
      )}
      {openId && (
        <SeriesDetailDrawer
          playlistId={openId}
          onClose={() => setOpenId(null)}
        />
      )}
    </>
  );
}

function SeriesCard({
  playlist,
  onOpen,
}: {
  playlist: YoutubePlaylistSummary;
  onOpen: () => void;
}) {
  const count = playlist.itemCount;
  return (
    <li className="yt-card is-playlist">
      <div className="yt-card-frame">
        <span className="yt-pl-badge" aria-hidden>
          <span>Series</span>
          <span className="yt-pl-badge-num">{count > 0 ? count : "—"}</span>
        </span>
        <span className="yt-pl-meta" aria-hidden>
          Open series →
        </span>
        <button
          type="button"
          className="yt-card-poster"
          onClick={onOpen}
          aria-label={`Open ${playlist.title} (${count} ${count === 1 ? "video" : "videos"})`}
        >
          {playlist.thumbnailUrl ? (
            <img
              src={playlist.thumbnailUrl}
              alt=""
              loading="lazy"
              decoding="async"
            />
          ) : (
            <span className="yt-row-thumb-empty">—</span>
          )}
          <span className="yt-stack-mark" aria-hidden />
          <span className="yt-play" aria-hidden>
            <Icon name="play" size="1.3em" />
          </span>
        </button>
      </div>
      <div className="yt-card-meta">
        <button
          type="button"
          className="yt-pl-title font-display text-[1.15rem] sm:text-[1.25rem] leading-[1.2] hover:text-[var(--color-violet)] transition-colors text-left"
          onClick={onOpen}
        >
          {playlist.title}
        </button>
        <p className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)] mt-1.5">
          {count > 0
            ? `${count} ${count === 1 ? "video" : "videos"} · series`
            : "series"}
        </p>
      </div>
    </li>
  );
}

function SeriesDetailDrawer({
  playlistId,
  onClose,
}: {
  playlistId: string;
  onClose: () => void;
}) {
  const q = useYoutubePlaylist(playlistId);
  const data = q.data;
  const playlist = data?.playlist;
  const items = data?.items ?? [];

  return (
    <Modal
      open
      onClose={onClose}
      title={playlist?.title ?? "Series"}
      eyebrow="Series"
      size="lg"
      headerActions={
        playlist && (
          <a
            href={playlistWatchUrl(playlist.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="row-action"
          >
            Open on YouTube ↗
          </a>
        )
      }
    >
      {q.isLoading ? (
        <p className="yt-admin-empty">Loading…</p>
      ) : !playlist ? (
        <p className="yt-admin-empty">Series not found.</p>
      ) : (
        <>
          {playlist.description && (
            <p className="yt-admin-note mb-5 max-w-[60ch]">
              {playlist.description}
            </p>
          )}
          <div className="yt-card-frame mb-6">
            <YoutubePlayer
              kind="playlist"
              id={playlist.id}
              title={`${playlist.title} — embedded series`}
            />
          </div>
          <p className="page-eyebrow mb-3">
            <span className="tabular">{items.length}</span>{" "}
            {items.length === 1 ? "item" : "items"} · updated{" "}
            {formatRelative(playlist.updatedAt)}
          </p>
          {items.length === 0 ? (
            <p className="yt-admin-empty">Empty.</p>
          ) : (
            <ul className="yt-pl-items">
              {items.map((it) => (
                <li key={it.id} className="yt-pl-item">
                  <span className="yt-pl-item-pos tabular">
                    {String(it.position + 1).padStart(2, "0")}
                  </span>
                  <img
                    src={it.thumbnailUrl}
                    alt=""
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="min-w-0">
                    <a
                      href={videoWatchUrl(it.videoId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="yt-pl-item-title"
                    >
                      {it.title}
                    </a>
                    <p className="yt-pl-item-meta">
                      <span className="tabular">
                        {formatDuration(it.durationSec)}
                      </span>
                      <span className="yt-row-sep">·</span>
                      <span>{formatRelative(it.publishedAt)}</span>
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Modal>
  );
}

function SkeletonGrid({ count }: { count: number }) {
  return (
    <ul className="yt-public-grid is-comfortable">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className="yt-card">
          <div className="yt-card-frame">
            <div className="skel-block w-full h-full" aria-hidden />
          </div>
          <div className="yt-card-meta">
            <div
              className="skel-block"
              style={{ width: "82%", height: 16, marginBottom: 8 }}
            />
            <div className="skel-block" style={{ width: "40%", height: 10 }} />
          </div>
        </li>
      ))}
    </ul>
  );
}
